import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Input } from '@/components/input/input'
import { useRestaurantCitySearchContext } from '@/features/restaurants/restaurants-city-search/utils/restaurant-city-search-context'
import { debounce } from '@/utils/functions/debounce'

interface IRestaurantCitySearchFiltersInputProps {
    setHasValue?: (hasValue: boolean) => void
}

export const RestaurantCitySearchFiltersInput = ({
    setHasValue,
}: IRestaurantCitySearchFiltersInputProps) => {
    const t = useTranslations()

    const {
        query: { name },
        setters: { setName },
    } = useRestaurantCitySearchContext()

    const [searchInput, setSearchInput] = useState(name ?? '')

    const hasSyncedRef = useRef(false)
    useEffect(() => {
        if (hasSyncedRef.current) return

        hasSyncedRef.current = true

        if (searchInput || !name) return

        setSearchInput(name)
    }, [name, searchInput])

    const debouncedSetName = useMemo(() => debounce(setName, 150), [setName])

    const onInputChanged = useCallback(
        (nextValue: string) => {
            setSearchInput(nextValue)

            setHasValue?.(nextValue !== '')

            const isInitialValue = searchInput === '' && nextValue.length === 1

            // When the user types the first character, we want to fetch results immediately
            // This is to make the search feel more responsive and to let the user know that the search is working
            // without having to click a button
            if (isInitialValue) {
                setName(nextValue)
            } else {
                debouncedSetName(nextValue)
            }
        },
        [debouncedSetName, searchInput, setName, setHasValue]
    )

    return (
        <Input
            styleType="white"
            type="search"
            shape="pill"
            value={searchInput}
            onValueChange={onInputChanged}
            inputClassName="min-w-full md:min-w-[342px]"
            placeholder={t('restaurants.city.searchPlaceholder')}
            containerClassName="w-full"
        />
    )
}
